import { defineStore } from "pinia";
import { pointApi } from "@/api";
import type { ApiResponse } from "@/api";
import { GAME_ID } from "./index";
import { useLanguageStore } from "./language";
import { useUserStore } from "./user";

// 任务类型
export enum TaskType {
  COMMENT_OR_LIKE = 1, // 评论或点赞
  DAILY_SIGN_IN = 3, // 每日签到
  DAILY_POST = 4, // 每日发帖
  BROWSE_TIME = 5, // 浏览时长
  FOLLOW_OFFICIAL_ACCOUNT = 6, // 关注官方账号
  UPDATE_AVATAR_USERNAME = 7, // 更新头像昵称
  TOTAL_SIGN_IN = 8, // 累计签到
}

export enum TaskActionType {
  CLICK = 1,
  BROWSE = 2,
}

export enum TaskStatus {
  IN_PROGRESS = 0,
  COMPLETED = 1,
}

export enum ExchangeStatus {
  AVAILABLE = 0,
  EXCHANGED = 1,
}

// 任务项类型
export interface TaskItem {
  id: number;
  task_name: string;
  event_key: number;
  points: number;
  task_icon: string;
  task_desc: string;
  action_url?: string;
  task_status: TaskStatus;
  [key: string]: any;
}

// 奖励项类型
export interface RewardItem {
  id: number;
  reward_name: string;
  reward_desc: string;
  reward_icon: string;
  reward_status: ExchangeStatus;
  points_cost: number;
  [key: string]: any;
}

// 兑换记录项类型
export interface ExchangeRecordItem {
  id: number;
  reward_id: number;
  reward_name: string;
  reward_icon: string;
  reward_type: number;
  points_cost: number;
  exchange_time: string;
  [key: string]: any;
}

export const usePointStore = defineStore("point", {
  state: () => ({
    // 任务列表
    taskList: [] as TaskItem[],
    // 奖励列表
    rewardList: [] as RewardItem[],
    // 兑换记录列表
    exchangeRecordList: [] as ExchangeRecordItem[],
    // 加载状态
    loading: false,
    // 错误信息
    error: null as string | null,
    // 浏览时长任务定时器
    browseTimer: null as NodeJS.Timeout | null,
    // 浏览时长任务ID
    browseTaskId: null as number | null,
  }),

  actions: {
    /**
     * 获取任务列表/进度
     */
    async getTasks(game_id: number = Number(GAME_ID)) {
      this.loading = true;
      this.error = null;

      try {
        const response = await pointApi.getTasks(
          game_id,
          useLanguageStore().targetLanguage
        );
        if (response.code == 200) {
          // 直接使用后端返回的数据
          this.taskList = response.data || [];
          // 检查是否有浏览时长任务需要启动定时器
          this.checkAndStartBrowseTimer();
          return response;
        }
      } catch (error) {
        this.error =
          error instanceof Error ? error.message : "获取任务列表失败";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 获取奖励列表
     */
    async getRewards(game_id: number = Number(GAME_ID)) {
      this.loading = true;
      this.error = null;

      try {
        const response = await pointApi.getRewards(
          game_id,
          useLanguageStore().targetLanguage
        );
        if (response.code == 200) {
          // 直接使用后端返回的数据
          this.rewardList = response.data || [];
          return response;
        }
      } catch (error) {
        this.error =
          error instanceof Error ? error.message : "获取奖励列表失败";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 设置奖励列表
     * @param rewards 奖励列表
     */
    setRewardList(rewards: RewardItem[]) {
      this.rewardList = rewards;
    },

    /**
     * 获取兑换记录
     * @param game_id - 游戏ID
     * @returns Promise<ApiResponse>
     */
    async getExchangeRecords(game_id: number = Number(GAME_ID)) {
      this.loading = true;
      this.error = null;

      try {
        const response = await pointApi.getExchangeRecords(
          game_id,
          useLanguageStore().targetLanguage
        );
        if (response.code == 200) {
          // 直接使用后端返回的数据
          this.exchangeRecordList = response.data.list || [];
          return response;
        }
      } catch (error) {
        this.error =
          error instanceof Error ? error.message : "获取兑换记录失败";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 记录任务行为完成
     * @param game_id - 游戏ID
     * @param task - 任务对象
     * @param action_type - 行为类型：1-点击任务, 2-浏览时长
     */
    async taskAction(
      task: TaskItem,
      action_type: TaskActionType,
      game_id: number = Number(GAME_ID)
    ) {
      this.error = null;
      try {
        const response = await pointApi.taskAction(
          game_id,
          task.id,
          action_type
        );
        if (response.code == 200) {
          // 重新请求任务列表以更新状态（有些任务需第 N 次点击才完成，不以本地为准）
          await this.getTasks(game_id);
          // 刷新用户信息以更新论坛币数量
          await useUserStore().getUserInfo();
          return response;
        }
      } catch (error) {
        this.error =
          error instanceof Error ? error.message : "记录任务行为失败";
        throw error;
      }
    },

    /**
     * 兑换奖励
     * @param reward_id - 奖励ID
     * @param game_id - 游戏ID
     * @returns Promise<ApiResponse>
     */
    async exchange(
      reward_id: number,
      server_id: number,
      role_id: number,
      game_id: number = Number(GAME_ID)
    ): Promise<ApiResponse> {
      this.error = null;

      try {
        const response = await pointApi.exchange(
          game_id,
          reward_id,
          server_id,
          role_id
        );
        if (response.code == 200) {
          // 兑换成功后，刷新奖励列表以更新兑换状态
          await this.getRewards(game_id);
          // 刷新用户信息以更新论坛币数量
          await useUserStore().getUserInfo();
        }
        return response;
      } catch (error) {
        this.error = error instanceof Error ? error.message : "兑换失败";
        throw error;
      }
    },

    /**
     * 获取浏览时长任务
     */
    getBrowseTask(): TaskItem | undefined {
      const taskList = Array.isArray(this.taskList)
        ? this.taskList
        : Array.from(Object.values(this.taskList));
      return taskList.find((task) => task.event_key === TaskType.BROWSE_TIME);
    },

    /**
     * 启动浏览时长任务定时器
     */
    startBrowseTimer(task: TaskItem) {
      // 清除可能存在的旧定时器
      this.clearBrowseTimer();
      const targetCount = task.target_count || 0;
      const durationMs = targetCount * 60 * 1000;
      this.browseTaskId = task.id;
      this.browseTimer = setTimeout(async () => {
        try {
          await this.taskAction(task, TaskActionType.BROWSE);
        } catch (error) {
          console.error("浏览时长任务自动完成失败:", error);
        } finally {
          this.clearBrowseTimer();
        }
      }, durationMs);
    },

    /**
     * 检查并启动浏览时长任务定时器
     */
    checkAndStartBrowseTimer() {
      const browseTask = this.getBrowseTask();
      // 任务不存在或已完成，清除定时器
      if (!browseTask || browseTask.task_status !== TaskStatus.IN_PROGRESS) {
        this.clearBrowseTimer();
        return;
      }

      // 没有目标时长，无法启动
      if (!browseTask.target_count) {
        return;
      }

      // 如果已有相同任务的定时器在运行，不需要重新启动
      if (this.browseTimer && this.browseTaskId === browseTask.id) {
        return;
      }

      // 启动新定时器
      this.startBrowseTimer(browseTask);
    },

    /**
     * 清除浏览时长任务定时器
     */
    clearBrowseTimer() {
      if (this.browseTimer) {
        clearTimeout(this.browseTimer);
        this.browseTimer = null;
      }
      this.browseTaskId = null;
    },
  },
});
